import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, ShieldCheck, Undo2, ChevronLeft, ChevronRight } from "lucide-react";

const itDate = (d) => (d ? new Date(`${d.slice(0, 10)}T12:00`).toLocaleDateString("it-IT") : "");

function Row({ s, ins, year, onSave }) {
  const [amount, setAmount] = useState("");
  return (
    <div className="p-3 sm:px-4 flex flex-wrap items-center gap-3" data-testid={`ins-row-${s.id}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="font-medium text-[#1C352D]">{s.last_name} {s.first_name} <span className="text-xs text-stone-400 font-normal">{s.matricola}</span></div>
        <div className="text-xs text-stone-500">{s.year_level ? `${s.year_level}° anno` : ""}{ins ? ` · pagata${ins.paid_at ? ` il ${itDate(ins.paid_at)}` : ""}${ins.amount ? ` · € ${ins.amount}` : ""}${ins.method ? ` · ${ins.method}` : ""}` : ""}</div>
      </div>
      <span className={`text-[11px] px-2 py-0.5 rounded-full ${ins ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`} data-testid={`ins-status-${s.id}`}>{ins ? "Assicurato" : "Non assicurato"}</span>
      {ins ? (
        <Button size="sm" variant="ghost" onClick={() => window.confirm("Annullare l'assicurazione registrata?") && onSave(s.id, { year, insured: false })} data-testid={`ins-undo-${s.id}`}><Undo2 size={14}/></Button>
      ) : (
        <div className="flex gap-1.5">
          <Input type="number" step="0.01" placeholder="€" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-8 w-20" data-testid={`ins-amount-${s.id}`}/>
          <Button size="sm" className="h-8 bg-emerald-700 hover:bg-emerald-800" onClick={() => onSave(s.id, { year, insured: true, amount: amount ? Number(amount) : null, method: "contanti" })} data-testid={`ins-pay-${s.id}`}><ShieldCheck size={13} className="mr-1"/>Pagata</Button>
        </div>
      )}
    </div>
  );
}

export default function Assicurazione() {
  const qc = useQueryClient();
  const [year, setYear] = useState(null);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const { data } = useQuery({ queryKey: ["insurance", year], queryFn: async () => (await api.get("/insurance", { params: year ? { year } : {} })).data });
  const save = useMutation({
    mutationFn: async ([id, body]) => (await api.put(`/insurance/${id}`, body)).data,
    onSuccess: () => { toast.success("Assicurazione aggiornata"); qc.invalidateQueries({ queryKey: ["insurance"] }); qc.invalidateQueries({ queryKey: ["students"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const y = data?.year;
  const rows = (data?.rows || []).filter((r) => (tab === "all" || (tab === "si") === !!r.insurance) &&
    (!q.trim() || `${r.student.last_name} ${r.student.first_name} ${r.student.matricola || ""}`.toLowerCase().includes(q.trim().toLowerCase())));
  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1100px] mx-auto space-y-5" data-testid="insurance-page">
      <PageHeader title="Assicurazione studenti" subtitle="Assicurazione annuale degli studenti del residenziale: scade per tutti il 31 dicembre."/>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-full border border-stone-200 bg-white px-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setYear((y || 0) - 1)} data-testid="ins-year-prev"><ChevronLeft size={15}/></Button>
          <span className="text-sm font-medium px-2" data-testid="ins-year">Anno {y}</span>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setYear((y || 0) + 1)} data-testid="ins-year-next"><ChevronRight size={15}/></Button>
        </div>
        <span className="text-xs text-stone-500">Scadenza {data?.expires}{y && data && y !== data.current_year ? " · non è l'anno in corso" : ""}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none"><div className="text-[11px] uppercase tracking-widest text-stone-500">Assicurati</div><div className="text-2xl font-semibold text-emerald-700" data-testid="ins-kpi-yes">{data?.insured ?? 0}</div></Card>
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none"><div className="text-[11px] uppercase tracking-widest text-stone-500">Non assicurati</div><div className="text-2xl font-semibold text-red-700" data-testid="ins-kpi-no">{data?.not_insured ?? 0}</div></Card>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={tab} onValueChange={setTab}><TabsList>
          <TabsTrigger value="all" data-testid="ins-tab-all">Tutti</TabsTrigger>
          <TabsTrigger value="no" data-testid="ins-tab-no">Non assicurati</TabsTrigger>
          <TabsTrigger value="si" data-testid="ins-tab-si">Assicurati</TabsTrigger>
        </TabsList></Tabs>
        <div className="relative flex-1 min-w-[200px]"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca studente o matricola" className="pl-8" data-testid="ins-search"/></div>
      </div>
      <Card className="border border-stone-200 rounded-xl shadow-none divide-y divide-stone-100">
        {rows.map((r) => <Row key={r.student.id} s={r.student} ins={r.insurance} year={y} onSave={(id, body) => save.mutate([id, body])}/>)}
        {!rows.length && <div className="p-8 text-center text-sm text-stone-400">Nessuno studente.</div>}
      </Card>
    </div>
  );
}
